import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { cartApi } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight, Loader2 } from "lucide-react";

export default function Cart() {
  const { user, loading } = useAuth();
  const { cart, refresh, updateQty } = useCart();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <div className="min-h-screen"><NavBar /><div className="text-center py-20">Loading…</div></div>;
  if (!user) return <Navigate to="/auth?mode=login" replace />;

  const checkout = async (mode) => {
    if (!cart?.items?.length) return;
    setBusy(mode);
    try {
      const r = await cartApi.checkout(mode, pickupTime || null, notes || "");
      if (mode === "stripe" && r?.url) {
        window.location.href = r.url;
        return;
      }
      if (mode === "pickup" && r?.preorders?.length) {
        await refresh();
        toast.success(`Locked ${r.preorders.length} pre-order${r.preorders.length > 1 ? "s" : ""}!`);
        nav("/me");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(""); }
  };

  const empty = !cart?.items?.length;

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -120, right: -80 }} />
      <NavBar />
      <Toaster position="top-right" theme="light" richColors />
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 relative">
        <span className="sticker sticker-magenta mb-3 inline-block"><ShoppingCart size={11} strokeWidth={2.4} /> Your bag</span>
        <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.9] tracking-tight mt-3 mb-8">
          The <span className="text-rainbow">cart.</span>
        </h1>

        {empty ? (
          <div className="card-x card-cyan bg-[#FAF6E8] p-12 text-center">
            <div className="text-7xl mb-4 bouncy">🛒</div>
            <h2 className="font-display text-3xl font-extrabold mb-2">Bag's empty.</h2>
            <p className="text-sm opacity-70 mb-6">Go find some gas.</p>
            <Link to="/discover" className="btn-x btn-primary">Discover <ArrowRight size={14} strokeWidth={2.4} /></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {cart.items.map((it) => (
                <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="card-x card-green bg-[#FAF6E8] p-4 flex items-center gap-4">
                  <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-2xl object-cover border-2 border-[#0B0F0C]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl font-extrabold leading-tight truncate">{it.name}</div>
                    <div className="text-xs font-mono-x font-bold uppercase tracking-widest opacity-60">{it.strain} · {it.category} · {it.thc}% THC</div>
                    <div className="font-mono-x text-sm font-bold mt-1">${it.price.toFixed(2)} ea</div>
                  </div>
                  <div className="flex items-center gap-1 border-2 border-[#0B0F0C] rounded-full bg-[#FAF6E8] px-1 h-9">
                    <button data-testid={`qty-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, Math.max(0, it.qty - 1))} className="w-7 h-7 flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white rounded-full"><Minus size={12} strokeWidth={2.4} /></button>
                    <span className="font-mono-x font-bold text-sm w-6 text-center">{it.qty}</span>
                    <button data-testid={`qty-inc-${it.product_id}`} onClick={() => updateQty(it.product_id, it.qty + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-[#15FF73] rounded-full"><Plus size={12} strokeWidth={2.4} /></button>
                  </div>
                  <button data-testid={`cart-remove-${it.product_id}`} onClick={() => updateQty(it.product_id, 0)} className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-[#0B0F0C] hover:bg-[#FF1A8C] hover:text-white"><Trash2 size={13} strokeWidth={2.4} /></button>
                </div>
              ))}
            </div>

            <aside className="card-x card-magenta bg-[#FAF6E8] p-6 lg:sticky lg:top-24 self-start">
              <div className="label-overline text-[#FF1A8C] mb-3">Order summary</div>
              <div className="font-display text-5xl font-extrabold leading-none mb-4">${cart.total.toFixed(2)}</div>
              <div className="text-sm opacity-70 mb-5">{cart.count} item{cart.count !== 1 ? "s" : ""} · across {new Set(cart.items.map((i) => i.dispensary_id)).size} shop{new Set(cart.items.map((i) => i.dispensary_id)).size !== 1 ? "s" : ""}</div>

              <input
                data-testid="cart-pickup"
                value={pickupTime} onChange={(e) => setPickupTime(e.target.value)}
                placeholder="Pickup time (e.g. 5pm today)"
                className="w-full bg-white border-2 border-[#0B0F0C] rounded-full px-4 h-11 text-sm font-medium outline-none focus:shadow-[3px_3px_0_#0B0F0C] transition-all mb-3"
              />
              <textarea
                data-testid="cart-notes"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                className="w-full bg-white border-2 border-[#0B0F0C] rounded-2xl px-4 py-3 text-sm font-medium outline-none focus:shadow-[3px_3px_0_#0B0F0C] transition-all mb-5 resize-none"
              />

              <button
                data-testid="cart-checkout-pickup"
                disabled={!!busy}
                onClick={() => checkout("pickup")}
                className="btn-x btn-primary w-full justify-center mb-2 disabled:opacity-50"
              >
                {busy === "pickup" ? <><Loader2 size={14} className="animate-spin" /> Reserving…</> : <>Reserve for pickup (free) <ArrowRight size={14} strokeWidth={2.4} /></>}
              </button>
              <button
                data-testid="cart-checkout-stripe"
                disabled={!!busy}
                onClick={() => checkout("stripe")}
                className="btn-x btn-ghost w-full justify-center disabled:opacity-50"
              >
                {busy === "stripe" ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <>Pay now with card →</>}
              </button>
              <p className="text-[10px] font-mono-x font-bold uppercase tracking-widest mt-4 opacity-55 text-center">21+ · ID at pickup · Stripe test mode</p>
            </aside>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
